import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiChevronLeft, FiChevronRight, FiCalendar } from "react-icons/fi";
import { useRoomCalendar } from "../../../../context/RoomCalendarContext";
import {
  getDaysInMonth,
  getEventsForDay,
  formatMonthYear,
  isToday,
} from "../../../../utils/calendarUtils";
import LoadingBar from "../../../ui/LoadingBar";
import "./CalendarMonthView.css";

// Day names
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Animation variants
const gridVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.01, delayChildren: 0.05 },
  },
};

const cellVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.15 } },
};

const eventVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.15 } },
  hover: { x: 4 },
};

const CalendarMonthView = ({
  roomId,
  events = [],
  onEventClick,
  eventTypes,
}) => {
  const { loading, fetchEvents } = useRoomCalendar();
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    if (roomId) {
      const loadEventsForMonth = async () => {
        const startOfMonth = new Date(
          currentMonth.getFullYear(),
          currentMonth.getMonth(),
          1
        );
        const endOfMonth = new Date(
          currentMonth.getFullYear(),
          currentMonth.getMonth() + 1,
          0,
          23,
          59,
          59
        );

        await fetchEvents(roomId, {
          startDate: startOfMonth.toISOString(),
          endDate: endOfMonth.toISOString(),
        });
      };
      loadEventsForMonth();
    }
  }, [roomId, currentMonth, fetchEvents]);

  const previousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  // Get days for the current month
  const days = useMemo(() => {
    return getDaysInMonth(currentMonth.getFullYear(), currentMonth.getMonth());
  }, [currentMonth]);

  // Group days into weeks
  const weeks = useMemo(() => {
    const result = [];
    for (let i = 0; i < days.length; i += 7) {
      result.push(days.slice(i, i + 7));
    }
    return result;
  }, [days]);

  // Get event type config
  const getEventConfig = (eventType) => {
    return (
      eventTypes?.[eventType] ||
      eventTypes?.other || { color: "#64748b", bg: "#f1f5f9" }
    );
  };

  if (loading && events.length === 0) {
    return <LoadingBar text="Loading calendar..." />;
  }

  return (
    <div className="rcmon-container">
      {/* Month Navigation */}
      <motion.div
        className="rcmon-header"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <motion.button
          className="rcmon-nav-btn"
          onClick={previousMonth}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          title="Previous Month"
        >
          <FiChevronLeft />
        </motion.button>

        <div className="rcmon-title-group">
          <h3 className="rcmon-title">{formatMonthYear(currentMonth)}</h3>
          <motion.button
            className="rcmon-today-btn"
            onClick={goToToday}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FiCalendar />
            <span>Today</span>
          </motion.button>
        </div>

        <motion.button
          className="rcmon-nav-btn"
          onClick={nextMonth}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          title="Next Month"
        >
          <FiChevronRight />
        </motion.button>
      </motion.div>

      {/* Day Names Header */}
      <div className="rcmon-day-names">
        {DAY_NAMES.map((day) => (
          <div key={day} className="rcmon-day-name">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <motion.div
        className="rcmon-grid"
        variants={gridVariants}
        initial="hidden"
        animate="visible"
        key={currentMonth.toISOString()}
      >
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="rcmon-week">
            {week.map((day, dayIndex) => {
              if (day === null) {
                return (
                  <motion.div
                    key={`empty-${dayIndex}`}
                    className="rcmon-cell rcmon-cell--empty"
                    variants={cellVariants}
                  />
                );
              }

              const date = new Date(
                currentMonth.getFullYear(),
                currentMonth.getMonth(),
                day
              );
              const dayEvents = getEventsForDay(events, date);
              const isTodayDate = isToday(date);

              return (
                <motion.div
                  key={day}
                  className={`rcmon-cell ${
                    isTodayDate ? "rcmon-cell--today" : ""
                  } ${dayEvents.length > 0 ? "rcmon-cell--has-events" : ""}`}
                  variants={cellVariants}
                  whileHover={{ scale: 1.02, zIndex: 2 }}
                >
                  <span
                    className={`rcmon-day-number ${
                      isTodayDate ? "rcmon-day-number--today" : ""
                    }`}
                  >
                    {day}
                  </span>

                  {/* Event Indicators */}
                  <div className="rcmon-events">
                    <AnimatePresence>
                      {dayEvents.slice(0, 3).map((event) => {
                        const config = getEventConfig(event.eventType);
                        return (
                          <motion.div
                            key={event._id}
                            className="rcmon-event"
                            style={{
                              "--event-color": config.color,
                              "--event-bg": config.bg,
                            }}
                            variants={eventVariants}
                            initial="hidden"
                            animate="visible"
                            whileHover="hover"
                            onClick={() => onEventClick && onEventClick(event)}
                            title={event.title}
                          >
                            <span
                              className="rcmon-event-dot"
                              style={{ background: config.color }}
                            />
                            <span className="rcmon-event-title">
                              {event.title.length > 12
                                ? `${event.title.substring(0, 12)}...`
                                : event.title}
                            </span>
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                    {dayEvents.length > 3 && (
                      <motion.div
                        className="rcmon-event-more"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                      >
                        +{dayEvents.length - 3} more
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        ))}
      </motion.div>

      {/* Legend */}
      <motion.div
        className="rcmon-legend"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <div className="rcmon-legend-item">
          <div className="rcmon-legend-dot rcmon-legend-dot--today" />
          <span>Today</span>
        </div>
        <div className="rcmon-legend-item">
          <div className="rcmon-legend-dot rcmon-legend-dot--has-events" />
          <span>Has Events</span>
        </div>
        {Object.entries(eventTypes || {})
          .slice(1, 5)
          .map(([key, config]) => (
            <div key={key} className="rcmon-legend-item">
              <div
                className="rcmon-legend-dot"
                style={{ background: config.color }}
              />
              <span>{config.label}</span>
            </div>
          ))}
      </motion.div>
    </div>
  );
};

export default CalendarMonthView;
